import { Modal, Button, Icon, ModalActions, Checkbox, Grid, Dimmer, Loader } from "semantic-ui-react";
import { useUniversalLateralMenuHook } from "../../hooks/useUniversalLateralMenuHook";
import { useDispatch, useSelector } from "react-redux";

import { useState, useEffect } from "react";
import userActions from './../../store/actions/users';
import { showSuccessMessage, showErrorMessage } from 'utils/messages';
import { useTranslation } from 'react-i18next';
import ThemeModeButton from 'components/ThemeModeButton';
const NOTIF_STORAGE_KEY = "notification_settings_v1";



export default function ModalMenu({ open, onClose, title, profile = "user" }) {
  const { t } = useTranslation()
  // Perfiles soportados: user, client, driver, supplier, employee
  const { lateralMenu, setLateralMenu, loading, synced } = useUniversalLateralMenuHook(profile);
  
  const NOTIFICATION_KEYS = [
    { key: "employee_license", label: t("Licencias") },
    { key: "documents_expiring", label: t("Documentos por vencer") },
    { key: "documents_expired", label: t("Documentos vencidos") },
    { key: "traffic_tickets", label: t("Multas de tráfico") },
    { key: "fast_cards", label: t("Tarjetas Fast") },
    { key: "waiting_orders", label: t("Órdenes en espera") },
    { key: "pending_orders", label: t("Órdenes pendientes") },
    { key: "delayed_orders", label: t("Órdenes atrasadas") },
    { key: "pending_articles", label: t("Artículos con pendiente") },
    { key: "programmed_orders", label: t("Órdenes programadas") },
    { key: "expired_service_orders", label: t("Órdenes expiradas") },
  ];

  // Selecciona el usuario actual según el perfil
  const currentUser = useSelector(state => {
    switch(profile) {
      case 'client':
        return state.app.client;
      case 'driver':
        return state.app.driver;
      case 'supplier':
        return state.app.supplier;
      case 'employee':
        return state.app.employee;
      default:
        return state.app.user;
    }
  });
  
  const dispatch = useDispatch();

  // Estado para fijar menú lateral
  const [tempLateralMenu, setTempLateralMenu] = useState(lateralMenu);

  // Estado para abrir procesos en otra pestaña (solo para user)
  const [tempUserConfig, setTempUserConfig] = useState({
    modules_target_blank_mode: currentUser?.modules_target_blank_mode ?? true,
    userId: currentUser?.id,
    id: currentUser?.id,
    sys_company_id: currentUser?.sys_company_id
  });

  // Estado para notificaciones (todos los perfiles)
  const [tempNotificationSettings, setTempNotificationSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(NOTIF_STORAGE_KEY + '_' + profile);
      if (saved) return JSON.parse(saved);
    } catch (e) { /* ignore */ }
    return NOTIFICATION_KEYS.reduce((acc, n) => ({ ...acc, [n.key]: true }), {});
  });
  useEffect(() => {
    setTempLateralMenu(lateralMenu);
    // Actualizar configuración del usuario actual según el perfil
    setTempUserConfig({
      modules_target_blank_mode: currentUser?.modules_target_blank_mode ?? true,
      userId: currentUser?.id,
      id: currentUser?.id,
      sys_company_id: currentUser?.sys_company_id
    });
    // when modal opens, reload local settings (so modal shows current saved config)
    try {
      const saved = localStorage.getItem(NOTIF_STORAGE_KEY + '_' + profile);
      if (saved) setTempNotificationSettings(JSON.parse(saved));
    } catch (e) {}

  }, [lateralMenu, open, profile, currentUser]);

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      size="mini"
      className="ThemeSettingsModal"
    >
      <div style={{ position: "relative" }}>
        <Icon
          name="close"
          style={{ position: "absolute", top: 10, right: 10, cursor: "pointer" }}
          onClick={onClose}
        />
      </div>
      <Modal.Header>
        {title}
        {loading && (
          <Icon name="sync" loading style={{ marginLeft: '10px', color: '#2185d0' }} />
        )}
        {synced && !loading && (
          <Icon name="check circle" style={{ marginLeft: '10px', color: '#00b5ad' }} />
        )}
      </Modal.Header>
      <Modal.Content>
        {loading && (
          <Dimmer active inverted>
            <Loader content={t("Sincronizando configuración...")} />
          </Dimmer>
        )}
        <Grid>
          {/* Configuración general */}
          {['Configuración', t('Configuración')].includes(title) && <>
            {/* Fijar menú lateral: disponible para todos los perfiles */}
            <Grid.Row columns={1}>
              <Grid.Column style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  toggle
                  label={t("Fijar menú lateral")}
                  checked={!!tempLateralMenu}
                  style={{ margin: '10px', display: 'block' }}
                  onChange={() => setTempLateralMenu(!tempLateralMenu)}
                />
              </Grid.Column>
            </Grid.Row>
            {/* Abrir procesos en otra pestaña: solo user */}
            {profile === 'user' && (
              <Grid.Row columns={1}>
                <Grid.Column style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    toggle
                    label={t("Abrir procesos del menú en otra pestaña del navegador")}
                    checked={!!tempUserConfig?.modules_target_blank_mode}
                    style={{ margin: "10px", display: "block" }}
                    onChange={() => {
                      setTempUserConfig(prev => ({
                        ...prev,
                        modules_target_blank_mode: !prev?.modules_target_blank_mode
                      }))
                    }}
                  />
                </Grid.Column>
              </Grid.Row>
            )}
            <Grid.Row columns={1}>
              <Grid.Column style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ margin: '10px' }}>
                  <ThemeModeButton />
                </div>
              </Grid.Column>
            </Grid.Row>
          </>}
          {/* Configuración de notificaciones */}
          {['Configuración de Notificaciones', t('Configuración de Notificaciones')].includes(title) && (
            <Grid.Row columns={1}>
              <Grid.Column>
                <p>{t('Seleccionar notificaciones')}</p>
                {NOTIFICATION_KEYS.map((n) => (
                  <Checkbox
                    toggle
                    key={n.key}
                    label={n.label}
                    checked={!!tempNotificationSettings[n.key]}
                    style={{ margin: "10px", display: "block" }}
                    onChange={() =>
                      setTempNotificationSettings((prev) => ({
                        ...prev,
                        [n.key]: !prev[n.key],
                      }))
                    }
                  />
                ))}
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>
      </Modal.Content>

      <ModalActions>
        <Button 
          disabled={loading}
          onClick={async () => {
            try {
              // Guardar notificaciones por perfil
              localStorage.setItem(NOTIF_STORAGE_KEY + '_' + profile, JSON.stringify(tempNotificationSettings));
              
              // No llamar al API si el ajuste del menú lateral no cambió.
              // Esto permite aplicar únicamente el tema desde una sesión impersonada.
              if (tempLateralMenu !== lateralMenu) {
                await setLateralMenu(tempLateralMenu);
              }
              
              // Guardar abrir procesos en otra pestaña solo si es usuario y hubo cambios
              if (profile === 'user') {
                const hasBlankModeChange =
                  tempUserConfig?.modules_target_blank_mode !== currentUser?.modules_target_blank_mode;

                if (hasBlankModeChange) {
                  await dispatch(userActions.updateUser(tempUserConfig, null));

                  if (hasBlankModeChange) {
                    await dispatch({
                      type: 'UPDATE_TARGET_ON_LINKS',
                      payload: tempUserConfig?.modules_target_blank_mode
                    });
                  }
                }
              }
              
              showSuccessMessage(t('Configuración guardada correctamente'));
              onClose();
            } catch (error) {
              console.error("Error al guardar configuración:", error);
              showErrorMessage(t('Error al guardar la configuración. Intente nuevamente.'));
            }
          }}
        >
          {loading ? t('Guardando...') : t('Aplicar Cambios')}
        </Button>
      </ModalActions>
    </Modal>
  );
}
